import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { MenuItem } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

// Font Awesome 6 free solid food icons: value stored as "fa-iconname"
// Display: if value starts with "fa-" → <i class="fa-solid fa-{name}"> else emoji
const FA_FOOD_ICONS: { cls: string; label: string }[] = [
  { cls: 'fa-burger', label: 'Burger' },
  { cls: 'fa-pizza-slice', label: 'Pizza' },
  { cls: 'fa-fish', label: 'Fish' },
  { cls: 'fa-drumstick-bite', label: 'Chicken' },
  { cls: 'fa-shrimp', label: 'Shrimp' },
  { cls: 'fa-hotdog', label: 'Hot Dog' },
  { cls: 'fa-bacon', label: 'Bacon' },
  { cls: 'fa-cheese', label: 'Cheese' },
  { cls: 'fa-egg', label: 'Egg' },
  { cls: 'fa-bread-slice', label: 'Bread' },
  { cls: 'fa-carrot', label: 'Carrot' },
  { cls: 'fa-apple-whole', label: 'Apple' },
  { cls: 'fa-lemon', label: 'Lemon' },
  { cls: 'fa-pepper-hot', label: 'Pepper' },
  { cls: 'fa-leaf', label: 'Vegetarian' },
  { cls: 'fa-seedling', label: 'Vegan' },
  { cls: 'fa-bowl-rice', label: 'Bowl' },
  { cls: 'fa-utensils', label: 'Entrée' },
  { cls: 'fa-cake-candles', label: 'Cake' },
  { cls: 'fa-cookie', label: 'Cookie' },
  { cls: 'fa-ice-cream', label: 'Ice Cream' },
  { cls: 'fa-wine-glass', label: 'Wine' },
  { cls: 'fa-beer-mug-empty', label: 'Beer' },
  { cls: 'fa-mug-hot', label: 'Coffee' },
  { cls: 'fa-mug-saucer', label: 'Tea' },
  { cls: 'fa-martini-glass', label: 'Cocktail' },
  { cls: 'fa-champagne-glasses', label: 'Champagne' },
  { cls: 'fa-blender', label: 'Smoothie' },
];

function renderFoodIcon(value: string, size = 28): string {
  if (value?.startsWith('fa-')) {
    return `<i class="fa-solid ${value}" style="font-size:${size}px;width:${size}px;text-align:center"></i>`;
  }
  return `<span style="font-size:${size}px">${value || '🍽'}</span>`;
}

export async function renderRestaurantMenu(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">🍽 Menu Management</h1>
          <p class="page-subtitle">Build and manage your restaurant menu. Tablets show this to customers.</p>
        </div>
        <div style="display:flex;gap:10px">
          <button class="btn btn-ghost" id="tablet-menu-btn">📱 Tablet Menu View</button>
          <button class="btn btn-ghost" id="ai-gen-btn">✦ AI Generate</button>
          <button class="btn btn-gold" id="add-item-btn">+ Add Item</button>
        </div>
      </div>
    </div>

    <!-- AI Generate Modal -->
    <div id="ai-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:560px;max-height:88vh;overflow-y:auto">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px">
          <i class="bi bi-stars"></i> AI Menu Generator
        </h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">
          Describe your concept, <strong>upload your existing menu</strong>, or take a photo — AI will extract and generate items.
        </p>

        <!-- Upload zone -->
        <div id="upload-zone" style="border:2px dashed var(--border);border-radius:var(--radius-md);padding:20px;text-align:center;cursor:pointer;margin-bottom:12px;transition:border-color .2s"
          ondragover="event.preventDefault();this.style.borderColor='var(--brand-gold)'"
          ondragleave="this.style.borderColor='var(--border)'"
          ondrop="event.preventDefault();this.style.borderColor='var(--border)'">
          <input type="file" id="menu-photo-input" accept="image/*" capture="environment" style="display:none" />
          <div style="font-size:28px;margin-bottom:8px">📷</div>
          <div style="font-size:13px;font-weight:600;margin-bottom:4px">Upload Menu Photo</div>
          <div style="font-size:11px;color:var(--text-muted);margin-bottom:12px">
            Drag & drop, click to browse, or use camera — JPG, PNG, WEBP
          </div>
          <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap">
            <button class="btn btn-ghost btn-sm" id="browse-photo-btn">
              <i class="bi bi-folder2-open"></i> Browse File
            </button>
            <button class="btn btn-ghost btn-sm" id="camera-btn">
              <i class="bi bi-camera"></i> Take Photo
            </button>
          </div>
        </div>

        <!-- Image preview -->
        <div id="photo-preview" style="display:none;margin-bottom:12px;text-align:center">
          <img id="preview-img" style="max-width:100%;max-height:200px;border-radius:var(--radius-sm);border:1px solid var(--border)" alt="Menu preview" />
          <div style="display:flex;gap:8px;justify-content:center;margin-top:8px">
            <button class="btn btn-ghost btn-sm" id="remove-photo-btn">
              <i class="bi bi-x-circle"></i> Remove Photo
            </button>
          </div>
        </div>

        <div style="text-align:center;font-size:11px;color:var(--text-muted);margin-bottom:12px">— or describe your menu —</div>

        <div class="form-row">
          <label><i class="bi bi-chat-left-text"></i> Cuisine / Restaurant Concept</label>
          <input type="text" id="ai-cuisine" placeholder="Modern American gastropub, Italian trattoria, Korean BBQ…" />
        </div>
        <div class="form-row">
          <label>Additional Instructions</label>
          <textarea id="ai-instructions" placeholder="Include vegetarian options, keep prices under $20, focus on shareable plates…" style="min-height:60px"></textarea>
        </div>

        <div id="ai-result" style="display:none;margin-top:12px">
          <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:8px">
            <i class="bi bi-list-check"></i> Generated Items — select to add:
          </div>
          <div id="ai-items-list"></div>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:12px">
          <button class="btn btn-ghost" id="close-ai-modal">Cancel</button>
          <button class="btn btn-gold" id="generate-menu-btn">
            <i class="bi bi-stars"></i> Generate Menu
          </button>
        </div>
      </div>
    </div>

    <!-- Add Item Modal -->
    <div id="item-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:500px;max-height:80vh;overflow-y:auto">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="modal-title">Add Menu Item</h3>
        <div class="form-row-2">
          <div>
            <label>Item Name *</label>
            <input type="text" id="item-name" placeholder="Grilled Salmon" />
          </div>
          <div>
            <label>Category *</label>
            <input type="text" id="item-category" placeholder="Entrées, Appetizers…" list="cat-list" />
            <datalist id="cat-list" id="item-modal-catlist"></datalist>
          </div>
        </div>
        <div class="form-row">
          <label>Description</label>
          <textarea id="item-desc" placeholder="Fresh Atlantic salmon, lemon butter, seasonal vegetables…" style="min-height:70px"></textarea>
        </div>
        <div class="form-row-2">
          <div>
            <label>Price ($) *</label>
            <input type="number" id="item-price" placeholder="0.00" min="0" step="0.01" />
          </div>
          <div>
            <label>Food Icon</label>
            <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:4px;margin-top:6px" id="emoji-picker">
              ${FA_FOOD_ICONS.map((ic) => `
                <button type="button" class="emoji-btn" data-emoji="${ic.cls}" title="${ic.label}"
                  style="padding:6px;border-radius:6px;border:1px solid var(--border);background:transparent;cursor:pointer;display:flex;align-items:center;justify-content:center;aspect-ratio:1">
                  <i class="fa-solid ${ic.cls}" style="font-size:16px;color:var(--text-secondary)"></i>
                </button>
              `).join('')}
            </div>
            <input type="hidden" id="item-emoji" value="fa-utensils" />
          </div>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-modal-btn">Cancel</button>
          <button class="btn btn-gold" id="save-item-btn">Save Item</button>
        </div>
      </div>
    </div>

    <div id="menu-content">
      <div class="empty-state"><div class="spinner"></div></div>
    </div>
  `;

  document.getElementById('add-item-btn')!.addEventListener('click', () => openModal());
  document.getElementById('tablet-menu-btn')!.addEventListener('click', () => navigate('restaurant-tablet'));
  document.getElementById('close-modal-btn')!.addEventListener('click', () => closeModal());
  document.getElementById('save-item-btn')!.addEventListener('click', () => saveItem(session?.businessId ?? 'preview', navigate));
  let uploadedPhotoBase64: string | null = null;

  document.getElementById('ai-gen-btn')!.addEventListener('click', () => {
    (document.getElementById('ai-cuisine') as HTMLInputElement).value = '';
    (document.getElementById('ai-instructions') as HTMLTextAreaElement).value = '';
    document.getElementById('ai-result')!.style.display = 'none';
    document.getElementById('photo-preview')!.style.display = 'none';
    uploadedPhotoBase64 = null;
    document.getElementById('ai-modal')!.style.display = 'flex';
  });
  document.getElementById('close-ai-modal')!.addEventListener('click', () => {
    document.getElementById('ai-modal')!.style.display = 'none';
    uploadedPhotoBase64 = null;
  });

  // Photo upload handlers
  const photoInput = document.getElementById('menu-photo-input') as HTMLInputElement;
  const setPhoto = (file: File) => {
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      uploadedPhotoBase64 = (e.target?.result as string).split(',')[1] ?? null;
      (document.getElementById('preview-img') as HTMLImageElement).src = e.target?.result as string;
      document.getElementById('photo-preview')!.style.display = 'block';
      document.getElementById('upload-zone')!.style.borderColor = 'var(--success)';
    };
    reader.readAsDataURL(file);
  };

  document.getElementById('browse-photo-btn')!.addEventListener('click', (e) => {
    e.stopPropagation();
    photoInput.removeAttribute('capture');
    photoInput.click();
  });
  document.getElementById('camera-btn')!.addEventListener('click', (e) => {
    e.stopPropagation();
    photoInput.setAttribute('capture', 'environment');
    photoInput.click();
  });
  document.getElementById('upload-zone')!.addEventListener('click', () => {
    photoInput.removeAttribute('capture');
    photoInput.click();
  });
  photoInput.addEventListener('change', () => {
    const file = photoInput.files?.[0];
    if (file) setPhoto(file);
  });
  document.getElementById('upload-zone')!.addEventListener('drop', (e) => {
    const file = (e as DragEvent).dataTransfer?.files[0];
    if (file) setPhoto(file);
  });
  document.getElementById('remove-photo-btn')!.addEventListener('click', () => {
    uploadedPhotoBase64 = null;
    photoInput.value = '';
    document.getElementById('photo-preview')!.style.display = 'none';
    document.getElementById('upload-zone')!.style.borderColor = 'var(--border)';
  });

  document.getElementById('generate-menu-btn')!.addEventListener('click', () =>
    generateAIMenu(session?.businessId ?? 'preview', navigate, uploadedPhotoBase64)
  );

  document.getElementById('emoji-picker')!.querySelectorAll('.emoji-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.getElementById('emoji-picker')!.querySelectorAll('.emoji-btn').forEach((b) => {
        (b as HTMLElement).style.background = 'transparent';
        (b as HTMLElement).style.borderColor = 'var(--border)';
      });
      (btn as HTMLElement).style.background = 'var(--brand-gold)22';
      (btn as HTMLElement).style.borderColor = 'var(--brand-gold)';
      (document.getElementById('item-emoji') as HTMLInputElement).value = btn.getAttribute('data-emoji') ?? 'fa-utensils';
    });
  });

  await loadMenu(session?.businessId ?? 'preview', navigate);
}

let editingItemId: string | null = null;

function openModal(item?: MenuItem): void {
  editingItemId = item?.id ?? null;
  (document.getElementById('modal-title') as HTMLElement).textContent = item ? 'Edit Item' : 'Add Menu Item';
  (document.getElementById('item-name') as HTMLInputElement).value = item?.name ?? '';
  (document.getElementById('item-category') as HTMLInputElement).value = item?.category ?? '';
  (document.getElementById('item-desc') as HTMLTextAreaElement).value = item?.description ?? '';
  (document.getElementById('item-price') as HTMLInputElement).value = item ? String(item.price) : '';
  (document.getElementById('item-emoji') as HTMLInputElement).value = item?.emoji ?? '🍽';
  const modal = document.getElementById('item-modal')!;
  modal.style.display = 'flex';
}

function closeModal(): void {
  document.getElementById('item-modal')!.style.display = 'none';
  editingItemId = null;
}

async function saveItem(businessId: string, navigate: NavigateFn): Promise<void> {
  const name = (document.getElementById('item-name') as HTMLInputElement).value.trim();
  const category = (document.getElementById('item-category') as HTMLInputElement).value.trim();
  const price = parseFloat((document.getElementById('item-price') as HTMLInputElement).value);

  if (!name || !category || isNaN(price)) { showToast('Name, category, and price are required', 'error'); return; }

  const payload = {
    businessId,
    name,
    category,
    description: (document.getElementById('item-desc') as HTMLTextAreaElement).value,
    price,
    emoji: (document.getElementById('item-emoji') as HTMLInputElement).value,
    available: true,
  };

  try {
    const api = getAPI();
    if (editingItemId) {
      await api.updateMenuItem(editingItemId, payload);
      showToast('Item updated', 'success');
    } else {
      await api.createMenuItem(payload);
      showToast('Item added', 'success');
    }
    closeModal();
    await loadMenu(businessId, navigate);
  } catch {
    showToast('Saved (preview mode)', 'info');
    closeModal();
  }
}

async function loadMenu(businessId: string, navigate: NavigateFn): Promise<void> {
  const content = document.getElementById('menu-content')!;
  let items: MenuItem[] = [];

  try {
    const api = getAPI();
    items = await api.getMenuItems(businessId) as MenuItem[];
  } catch {
    items = getSampleMenu(businessId);
  }

  if (items.length === 0) {
    content.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🍽</div>
        <div class="empty-state-title">Menu is Empty</div>
        <div class="empty-state-desc">Add your first menu item to get started.</div>
      </div>
    `;
    return;
  }

  const categories = [...new Set(items.map((i) => i.category))].sort();

  content.innerHTML = categories.map((cat) => {
    const catItems = items.filter((i) => i.category === cat);
    return `
      <div class="card" style="margin-bottom:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px">
          <h3 style="font-size:14px;font-weight:700">${escHtml(cat)}</h3>
          <span style="font-size:11px;color:var(--text-muted)">${catItems.length} item${catItems.length !== 1 ? 's' : ''}</span>
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:10px">
          ${catItems.map((item) => `
            <div class="menu-item-card" data-id="${item.id}" style="opacity:${item.available ? 1 : .5}">
              <div style="display:flex;align-items:center;gap:10px">
                <div>${renderFoodIcon(item.emoji ?? 'fa-utensils')}</div>
                <div style="flex:1;min-width:0">
                  <div style="font-weight:600;font-size:13px">${escHtml(item.name)}</div>
                  ${item.description ? `<div style="font-size:11px;color:var(--text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(item.description)}</div>` : ''}
                </div>
                <div style="font-size:15px;font-weight:700;color:var(--brand-gold);flex-shrink:0">$${item.price.toFixed(2)}</div>
              </div>
              <div style="display:flex;gap:6px;margin-top:10px;padding-top:10px;border-top:1px solid var(--border)">
                <button class="btn btn-ghost btn-sm" data-action="edit" data-id="${item.id}" style="flex:1">Edit</button>
                <button class="btn btn-ghost btn-sm" data-action="toggle" data-id="${item.id}" style="flex:1">${item.available ? '⛔ 86 It' : '✓ Available'}</button>
                <button class="btn btn-ghost btn-sm" data-action="delete" data-id="${item.id}" style="color:var(--danger)">✕</button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }).join('');

  content.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const action = btn.getAttribute('data-action')!;
      const id = btn.getAttribute('data-id')!;
      if (action === 'edit') {
        const item = items.find((i) => i.id === id);
        if (item) openModal(item);
      } else if (action === 'toggle') {
        const item = items.find((i) => i.id === id);
        if (item) {
          try { await getAPI().updateMenuItem(id, { available: !item.available }); } catch { /* preview */ }
          await loadMenu(businessId, navigate);
        }
      } else if (action === 'delete') {
        if (!confirm('Remove this item?')) return;
        try { await getAPI().deleteMenuItem(id); } catch { /* preview */ }
        await loadMenu(businessId, navigate);
      }
    });
  });
}

interface AIMenuItem { name: string; description: string; price: number; category: string; emoji: string; }

async function generateAIMenu(businessId: string, navigate: NavigateFn, photoBase64: string | null): Promise<void> {
  const cuisine = (document.getElementById('ai-cuisine') as HTMLInputElement).value.trim();
  const instructions = (document.getElementById('ai-instructions') as HTMLTextAreaElement).value.trim();
  if (!cuisine && !photoBase64) {
    showToast('Please describe your cuisine or upload a menu photo', 'error');
    return;
  }

  const btn = document.getElementById('generate-menu-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<i class="bi bi-hourglass-split"></i> Generating…';

  const photoNote = photoBase64
    ? `A menu photo has been uploaded. Extract all menu items visible in the image, inferring prices and descriptions where not fully legible.${cuisine ? ` The restaurant concept is: "${cuisine}".` : ''}`
    : `Generate a menu for: "${cuisine}".`;

  const prompt = `You are a restaurant consultant. ${photoNote}
${instructions ? `Additional requirements: ${instructions}` : ''}

Return ONLY a valid JSON array of 8-12 menu items. Each item must have:
- name (string): item name
- description (string): 1-2 sentence description
- price (number): price in USD (no $ sign)
- category (string): one of Appetizers, Soups & Salads, Entrees, Sandwiches & Burgers, Pasta, Pizza, Sides, Desserts, Drinks
- emoji (string): single food emoji

Example format:
[{"name":"Bruschetta","description":"Toasted bread with fresh tomatoes and basil.","price":9.99,"category":"Appetizers","emoji":"🍅"}]

Return only the JSON array, no other text.`;

  let generatedItems: AIMenuItem[] = [];

  try {
    const api = getAPI() as Record<string, (...args: unknown[]) => Promise<unknown>>;
    const result = await api.sendToAI({
      prompt,
      documentTitle: 'Menu Generation',
      provider: photoBase64 ? 'anthropic' : 'huggingface',
      model: photoBase64 ? 'claude-haiku-4-5-20251001' : 'mistral-7b',
      ...(photoBase64 ? { imageBase64: photoBase64, imageMimeType: 'image/jpeg' } : {}),
    }) as { aiResponse: string };

    const text = result.aiResponse ?? '';
    const match = text.match(/\[[\s\S]*\]/);
    if (match) {
      generatedItems = JSON.parse(match[0]) as AIMenuItem[];
    }
  } catch {
    generatedItems = getFallbackMenuItems(cuisine || 'restaurant');
  }

  btn.disabled = false;
  btn.innerHTML = '<i class="bi bi-stars"></i> Generate Menu';

  if (!generatedItems.length) {
    showToast('Could not generate menu items. Try again.', 'error');
    return;
  }

  const resultEl = document.getElementById('ai-result')!;
  const listEl = document.getElementById('ai-items-list')!;
  resultEl.style.display = 'block';

  listEl.innerHTML = generatedItems.map((item, i) => `
    <div style="display:flex;align-items:center;gap:10px;padding:8px;border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:6px;cursor:pointer;transition:background .15s" class="ai-item-row" data-idx="${i}">
      <input type="checkbox" id="ai-item-${i}" checked style="flex-shrink:0" />
      <label for="ai-item-${i}" style="display:flex;align-items:center;gap:8px;flex:1;cursor:pointer">
        ${renderFoodIcon(item.emoji, 22)}
        <div style="flex:1">
          <div style="font-weight:600;font-size:13px">${escHtml(item.name)} <span style="color:var(--brand-gold)">$${Number(item.price).toFixed(2)}</span></div>
          <div style="font-size:11px;color:var(--text-muted)">${escHtml(item.category)} · ${escHtml(item.description)}</div>
        </div>
      </label>
    </div>
  `).join('');

  // Replace Generate button with "Add Selected" button
  const footer = document.querySelector('#ai-modal .card > div:last-child')!;
  footer.innerHTML = `
    <button class="btn btn-ghost" id="close-ai-modal-2">Cancel</button>
    <button class="btn btn-primary" id="select-all-ai">Select All</button>
    <button class="btn btn-gold" id="add-ai-items-btn">Add Selected to Menu</button>
  `;

  document.getElementById('close-ai-modal-2')!.addEventListener('click', () => {
    document.getElementById('ai-modal')!.style.display = 'none';
  });
  document.getElementById('select-all-ai')!.addEventListener('click', () => {
    listEl.querySelectorAll('input[type="checkbox"]').forEach((cb) => { (cb as HTMLInputElement).checked = true; });
  });
  document.getElementById('add-ai-items-btn')!.addEventListener('click', async () => {
    const selected = generatedItems.filter((_, i) => {
      const cb = document.getElementById(`ai-item-${i}`) as HTMLInputElement | null;
      return cb?.checked ?? true;
    });
    if (!selected.length) { showToast('Select at least one item', 'error'); return; }

    const addBtn = document.getElementById('add-ai-items-btn') as HTMLButtonElement;
    addBtn.disabled = true;
    addBtn.innerHTML = '<span class="spinner"></span> Adding…';

    let added = 0;
    for (const item of selected) {
      try {
        await getAPI().createMenuItem({
          businessId,
          name: item.name,
          description: item.description,
          price: Number(item.price),
          category: item.category,
          emoji: item.emoji,
          available: true,
        });
        added++;
      } catch { added++; /* preview */ }
    }

    showToast(`${added} items added to your menu`, 'success');
    document.getElementById('ai-modal')!.style.display = 'none';
    await loadMenu(businessId, navigate);
  });
}

function getFallbackMenuItems(cuisine: string): AIMenuItem[] {
  return [
    { name: 'House Special Starter', description: `A ${cuisine} inspired starter plate with seasonal ingredients.`, price: 12.99, category: 'Appetizers', emoji: '🥗' },
    { name: 'Signature Soup', description: 'Chef\'s daily soup made from scratch.', price: 8.99, category: 'Soups & Salads', emoji: '🍲' },
    { name: 'Main Course', description: `The flagship ${cuisine} dish, prepared fresh.`, price: 22.99, category: 'Entrees', emoji: '🍽' },
    { name: 'Grilled Protein', description: 'Seasoned and grilled to perfection.', price: 26.99, category: 'Entrees', emoji: '🥩' },
    { name: 'Vegetarian Option', description: 'Fresh vegetables prepared in our signature style.', price: 16.99, category: 'Entrees', emoji: '🥦' },
    { name: 'House Side', description: 'Seasonal side dish.', price: 5.99, category: 'Sides', emoji: '🥔' },
    { name: 'Signature Dessert', description: 'House-made dessert, served warm.', price: 8.99, category: 'Desserts', emoji: '🍮' },
    { name: 'House Drink', description: 'Fresh-made specialty beverage.', price: 4.99, category: 'Drinks', emoji: '🥤' },
  ];
}

function getSampleMenu(businessId: string): MenuItem[] {
  return [
    { id: '1', businessId, name: 'Classic Burger', description: 'Angus beef, cheddar, lettuce, tomato', price: 14.99, category: 'Burgers', available: true, emoji: '🍔' },
    { id: '2', businessId, name: 'BBQ Bacon Burger', description: 'Crispy bacon, BBQ sauce, onion rings', price: 17.99, category: 'Burgers', available: true, emoji: '🍔' },
    { id: '3', businessId, name: 'Caesar Salad', description: 'Romaine, parmesan, house-made dressing', price: 11.99, category: 'Salads', available: true, emoji: '🥗' },
    { id: '4', businessId, name: 'Grilled Salmon', description: 'Atlantic salmon, lemon butter, asparagus', price: 24.99, category: 'Entrées', available: true, emoji: '🍣' },
    { id: '5', businessId, name: 'Chicken Alfredo', description: 'Grilled chicken, house-made alfredo, fettuccine', price: 18.99, category: 'Entrées', available: true, emoji: '🍝' },
    { id: '6', businessId, name: 'Mozzarella Sticks', description: 'Fried mozz, marinara dipping sauce', price: 8.99, category: 'Appetizers', available: true, emoji: '🧀' },
    { id: '7', businessId, name: 'Craft Lemonade', description: 'Fresh squeezed, seasonal flavors', price: 4.99, category: 'Drinks', available: true, emoji: '🍋' },
    { id: '8', businessId, name: 'Chocolate Lava Cake', description: 'Warm dark chocolate, vanilla ice cream', price: 8.99, category: 'Desserts', available: true, emoji: '🍫' },
  ];
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
